import React, { useEffect, useMemo } from 'react';
import {
	Alert,
	Box,
	Button,
	IconButton,
	Skeleton,
	Stack,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import SettingsNav from './components/SettingsNav';
import SettingsOrgProfile from './components/SettingsOrgProfile';
import { PAGE_CONTAINER_SX } from './components/SettingsShell';
import useOrganizationSettings from './hooks/useOrganizationSettings';
import { SETTINGS_SECTIONS } from './constants';
import CompanySection from './sections/CompanySection';
import ProposalsSection from './sections/ProposalsSection';
import PaymentsSection from './sections/PaymentsSection';
import WorkflowSection from './sections/WorkflowSection';
import TimeTrackingSection from './sections/TimeTrackingSection';
import SmsAlertsSection from './sections/SmsAlertsSection';
import BillingSection from './sections/BillingSection';
import UpgradeSeatSelectionModal from '../../Components/Modals/UpgradeSeatSelectionModal';
import UpgradePlanConfirmModal from '../../Components/Modals/UpgradePlanConfirmModal';
import DowngradeSeatPickerModal from '../../Components/Modals/DowngradeSeatPickerModal';
import CancelSubscriptionModal from '../../Components/Modals/CancelSubscriptionModal';
import { ACTION_TYPES, useAppContext } from '../../Context/AppContext';
import { openChoosePlan } from '../../Shared/trial';

const Settings = () => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const { dispatch } = useAppContext();
	const settings = useOrganizationSettings();

	const trialActive = Boolean(
		settings.subscription?.trial_active ??
			settings.organization?.trial_active,
	);

	const navItems = useMemo(() => {
		const items = [
			{
				id: SETTINGS_SECTIONS.company,
				label: 'Company',
				description: 'Logo, contact, mailing address',
				icon: BusinessOutlinedIcon,
			},
			{
				id: SETTINGS_SECTIONS.proposals,
				label: 'Proposals',
				description: 'Defaults and reminders',
				icon: DescriptionOutlinedIcon,
			},
			{
				id: SETTINGS_SECTIONS.payments,
				label: 'Payments',
				description: 'Venmo, Stripe, previews',
				icon: PaymentsOutlinedIcon,
			},
		];
		items.push({
			id: SETTINGS_SECTIONS.workflow,
			label: 'Workflow',
			description: 'Job statuses',
			icon: ViewListOutlinedIcon,
		});
		if (settings.canUseTimeTracking) {
			items.push({
				id: SETTINGS_SECTIONS.time,
				label: 'Time',
				description: 'Shop location and clock-in',
				icon: AccessTimeOutlinedIcon,
			});
		}
		if (settings.canUseSmsNotifications) {
			items.push({
				id: SETTINGS_SECTIONS.sms,
				label: 'SMS',
				description: 'Manager alert phone and triggers',
				icon: SmsOutlinedIcon,
			});
		}
		if (settings.showSubscriptionSection) {
			items.push({
				id: SETTINGS_SECTIONS.billing,
				label: 'Billing',
				description: trialActive
					? 'Free trial and upgrade'
					: 'Plan and subscription',
				icon: CreditCardOutlinedIcon,
			});
		}
		return items;
	}, [
		settings.canUseTimeTracking,
		settings.canUseSmsNotifications,
		settings.showSubscriptionSection,
		trialActive,
	]);

	const mobileNavItems = useMemo(
		() => navItems.filter((item) => item.id !== SETTINGS_SECTIONS.company),
		[navItems],
	);

	const desktopSection =
		settings.activeSection || SETTINGS_SECTIONS.company;
	const mobileInSection = isMobile && Boolean(settings.activeSection);
	const displaySection = isMobile
		? settings.activeSection
		: desktopSection;
	const sectionLabels = Object.fromEntries(
		navItems.map((item) => [item.id, item.label]),
	);
	const sectionTitle = displaySection
		? sectionLabels[displaySection]
		: null;

	useEffect(() => {
		const title =
			mobileInSection && sectionTitle ? sectionTitle : 'Settings';
		dispatch({
			type: ACTION_TYPES.SET_PAGE_TITLE,
			payload: { pageTitle: title },
		});
	}, [dispatch, mobileInSection, sectionTitle]);

	if (settings.loading) {
		return (
			<Box sx={PAGE_CONTAINER_SX}>
				<Stack spacing={3}>
					<Box>
						<Skeleton variant="text" width={180} height={40} />
						<Skeleton variant="text" width={320} />
					</Box>
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: { xs: '1fr', md: '220px 1fr' },
							gap: 3,
						}}
					>
						<Skeleton variant="rounded" height={280} />
						<Skeleton variant="rounded" height={480} />
					</Box>
				</Stack>
			</Box>
		);
	}

	const renderSection = (section) => {
		switch (section) {
			case SETTINGS_SECTIONS.proposals:
				return (
					<ProposalsSection
						formData={settings.formData}
						setFormData={settings.setFormData}
						saving={settings.saving}
						onSave={settings.handleSave}
					/>
				);
			case SETTINGS_SECTIONS.payments:
				return (
					<PaymentsSection
						formData={settings.formData}
						setFormData={settings.setFormData}
						saving={settings.saving}
						stripeReady={settings.stripeReady}
						stripeConnected={settings.stripeConnected}
						connecting={settings.connecting}
						stripeStatusMessage={settings.stripeStatusMessage}
						paymentPreview={settings.paymentPreview}
						venmoPreview={settings.venmoPreview}
						emailFooterPreview={settings.emailFooterPreview}
						sampleEmailTooltip={settings.sampleEmailTooltip}
						sendingSampleBid={settings.sendingSampleBid}
						sendingSampleInvoice={settings.sendingSampleInvoice}
						userEmail={settings.state.user.email}
						onInputChange={settings.handleInputChange}
						onSave={settings.handleSave}
						onConnectStripe={settings.handleConnectStripe}
						onSendSampleBid={settings.handleSendSampleBid}
						onSendSampleInvoice={settings.handleSendSampleInvoice}
					/>
				);
			case SETTINGS_SECTIONS.workflow:
				return (
					<WorkflowSection
						onSuccess={settings.handleJobStatusSuccess}
						onError={(message) =>
							settings.enqueueSnackbar(message, { variant: 'error' })
						}
					/>
				);
			case SETTINGS_SECTIONS.time:
				return (
					<TimeTrackingSection
						formData={settings.formData}
						setFormData={settings.setFormData}
						saving={settings.saving}
						canUseTimeTracking={settings.canUseTimeTracking}
						onSave={settings.handleSave}
					/>
				);
			case SETTINGS_SECTIONS.sms:
				return (
					<SmsAlertsSection
						formData={settings.formData}
						setFormData={settings.setFormData}
						saving={settings.saving}
						canUseSmsNotifications={settings.canUseSmsNotifications}
						savedPhone={settings.organization?.sms_alert_phone || ''}
						allowedTriggers={settings.organization?.sms_alert_triggers}
						onSave={settings.handleSave}
					/>
				);
			case SETTINGS_SECTIONS.billing:
				return (
					<BillingSection
						subscription={settings.subscription}
						subscriptionLoading={settings.subscriptionLoading}
						subscriptionStatus={settings.subscriptionStatus}
						subscriptionExempt={settings.subscriptionExempt}
						organizationName={settings.organization?.name}
						currentPlanTier={settings.currentPlanTier}
						openingBillingPortal={settings.openingBillingPortal}
						reactivating={settings.reactivating}
						cancelingScheduledDowngrade={settings.cancelingScheduledDowngrade}
						onManageBilling={settings.handleManageBilling}
						onUpgradeToPro={settings.handleUpgradeToPro}
						onPrepareDowngrade={settings.handlePrepareDowngrade}
						onCancelSubscription={settings.handleCancelSubscription}
						onReactivateSubscription={settings.handleReactivateSubscription}
						onCancelScheduledDowngrade={settings.handleCancelScheduledDowngrade}
						trialActive={trialActive}
						trialEndsAt={
							settings.subscription?.trial_ends_at ||
							settings.organization?.trial_ends_at
						}
						onChoosePlan={openChoosePlan}
					/>
				);
			case SETTINGS_SECTIONS.company:
			default:
				return (
					<CompanySection
						organization={settings.organization}
						formData={settings.formData}
						saving={settings.saving}
						stripeReady={settings.stripeReady}
						onInputChange={settings.handleInputChange}
						onSave={settings.handleSave}
						onOrganizationUpdated={settings.handleOrganizationUpdated}
						onSuccess={(message) =>
							settings.enqueueSnackbar(message, { variant: 'success' })
						}
						onError={(message) =>
							settings.enqueueSnackbar(message, { variant: 'error' })
						}
					/>
				);
		}
	};

	const alerts = (
		<>
			{settings.loadError ? (
				<Alert severity="error">{settings.loadError}</Alert>
			) : null}

			{settings.isManager && settings.onboardingDismissed ? (
				<Alert
					severity="info"
					action={
						<Button
							color="inherit"
							size="small"
							onClick={settings.handleRestoreOnboarding}
							disabled={settings.restoringOnboarding}
						>
							{settings.restoringOnboarding
								? 'Restoring...'
								: 'Show setup guide'}
						</Button>
					}
				>
					The setup guide is hidden. Bring it back if you still have steps to
					complete.
				</Alert>
			) : null}

			{settings.isManager && settings.proOnboardingDismissed ? (
				<Alert
					severity="info"
					action={
						<Button
							color="inherit"
							size="small"
							onClick={settings.handleRestoreProOnboarding}
							disabled={settings.restoringProOnboarding}
						>
							{settings.restoringProOnboarding
								? 'Restoring...'
								: 'Show Pro setup guide'}
						</Button>
					}
				>
					The Pro ops setup guide is hidden. Bring it back if you still have Pro
					steps to complete.
				</Alert>
			) : null}
		</>
	);

	return (
		<Box sx={PAGE_CONTAINER_SX}>
			<Stack spacing={{ xs: 2, sm: 2.5 }}>
				{mobileInSection ? (
					<>
						<Box>
							<IconButton
								edge="start"
								onClick={settings.handleClearSection}
								aria-label="Back to settings"
								size="small"
							>
								<ArrowBackIcon />
							</IconButton>
						</Box>
						{renderSection(displaySection)}
					</>
				) : (
					<>
						{alerts}

						{isMobile ? (
							<>
								<SettingsOrgProfile
									organization={settings.organization}
									planLabel={settings.currentPlanTier?.name}
									onClick={() =>
										settings.handleSectionChange(SETTINGS_SECTIONS.company)
									}
								/>
								<SettingsNav
									items={mobileNavItems}
									activeSection={settings.activeSection}
									onSectionChange={settings.handleSectionChange}
								/>
							</>
						) : (
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									gap: 3,
									alignItems: 'flex-start',
								}}
							>
								<SettingsNav
									items={navItems}
									activeSection={desktopSection}
									onSectionChange={settings.handleSectionChange}
								/>
								<Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
									{renderSection(desktopSection)}
								</Box>
							</Box>
						)}
					</>
				)}
			</Stack>

			<UpgradeSeatSelectionModal
				open={settings.upgradeModalOpen}
				onClose={() => settings.setUpgradeModalOpen(false)}
				onSuccess={settings.handleBillingFlowSuccess}
				enqueueSnackbar={settings.enqueueSnackbar}
			/>
			<UpgradePlanConfirmModal
				open={settings.upgradePlanModalOpen}
				onClose={() => settings.setUpgradePlanModalOpen(false)}
				onSuccess={settings.handleBillingFlowSuccess}
				enqueueSnackbar={settings.enqueueSnackbar}
				prepaidCommitActive={Boolean(
					settings.subscription?.prepaid_commit_active,
				)}
			/>
			<DowngradeSeatPickerModal
				open={settings.downgradeModalOpen}
				onClose={() => settings.setDowngradeModalOpen(false)}
				onSuccess={settings.handleBillingFlowSuccess}
				enqueueSnackbar={settings.enqueueSnackbar}
			/>
			<CancelSubscriptionModal
				open={settings.cancelModalOpen}
				onClose={() => settings.setCancelModalOpen(false)}
				onSuccess={settings.handleBillingFlowSuccess}
				enqueueSnackbar={settings.enqueueSnackbar}
				periodEnd={settings.subscription?.current_period_end}
			/>
		</Box>
	);
};

export default Settings;
